import { Header, Segment } from '@mairistem/composants';
import { GenericTextOperators } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { ETATS_FACTURE } from '@/constants';
import { FacturePrelevementContext, FactureRejetContext } from '@/contexts';
import type { FactureListePrelevementItemType } from '@/types/facture';
import { formatFacturesToPrelevementListeItem } from '@/utils/facture';
import ModalPrelevementRejet from './ModalPrelevementRejet';
import ModalPrelevementValidation from './ModalPrelevementValidation';
import PrelevementsListe from './PrelevementsListe';
import RejetsListe from './RejetsListe';

type PrelevementTabProps = {
  onRejeterClick?: (rejets: FactureListePrelevementItemType[], motif: string) => Promise<boolean>;
  onRejetDelete?: (rejet: FactureListePrelevementItemType) => Promise<boolean>;
  onValiderClick?: (validees: FactureListePrelevementItemType[]) => Promise<boolean>;
  mode: 'rejeter' | 'valider' | 'rejets';
  reset?: boolean;
};

const PrelevementTab = (props: PrelevementTabProps) => {
  const { onRejeterClick, onRejetDelete, onValiderClick, mode, reset } = props;

  const [selectedItems, setSelectedItems] = React.useState<FactureListePrelevementItemType[]>([]);
  const [selectedCycle, setSelectedCycle] = React.useState<number>(0);

  const [openRejet, setOpenRejet] = React.useState<boolean>(false);
  const [openValidation, setOpenValidation] = React.useState<boolean>(false);

  const context = mode === 'rejets' ? FactureRejetContext : FacturePrelevementContext;

  const { factures, setFilters, setPage, applyDefaultFilters } = React.useContext(context);

  const formatedFactures = React.useMemo(
    () => formatFacturesToPrelevementListeItem(factures),
    [factures],
  );

  // Somme de tous les montants des lignes des prélèvements sélectionnés
  const montantTotal = React.useMemo(
    () => _.sumBy(mode === 'rejeter' ? selectedItems : formatedFactures, 'montant'),
    [formatedFactures, mode, selectedItems],
  );

  const handleItemSelect = React.useCallback((selection: FactureListePrelevementItemType[]) => {
    setSelectedItems(selection);
  }, []);

  const handleCycleSelect = React.useCallback(
    (value) => {
      setSelectedCycle(value);
      setPage(1);
      if (value === 0) {
        applyDefaultFilters();
      } else {
        setFilters([
          {
            operator: 'DANS_LISTE',
            textOperator: GenericTextOperators.DANS_LISTE,
            propertyName: 'identifiantCycle',
            value: [value],
            // Ci-dessous, normalement pas utilisé mais obligatoire pour TS
            type: 'number',
            predicate: null,
          },
        ]);
      }
    },
    [applyDefaultFilters, setFilters, setPage],
  );

  const handleRejetClose = React.useCallback(() => {
    setOpenRejet(false);
  }, []);

  const handleRejetOpen = React.useCallback(() => {
    setOpenRejet(true);
  }, []);

  const handleRejeterClick = React.useCallback(
    async (motif) => {
      const response = await onRejeterClick(selectedItems, motif);

      if (response) {
        toast(
          _.size(selectedItems) === 1
            ? '1 prélèvement rejeté avec succès'
            : `${_.size(selectedItems)} prélèvements rejetés avec succès`,
          { type: 'success' },
        );

        setOpenRejet(false);
        setSelectedItems([]);
        setSelectedCycle(0);

        return true;
      }

      return false;
    },
    [onRejeterClick, selectedItems],
  );

  const handleRejetDelete = React.useCallback(
    async (rejet: FactureListePrelevementItemType) => {
      const facture = _.find(factures, { identifiant: rejet.identifiant });
      const hasRecus = _.some(facture.recus, { dateAnnulation: null });

      if (hasRecus) {
        toast(
          <Header as="h4" style={{ margin: 0 }}>
            Suppression du rejet impossible
          </Header>,
          { type: 'error' },
        );
        return false;
      }

      if (facture.etat === ETATS_FACTURE.ENCAISSEE) {
        toast(
          <Header as="h4" style={{ margin: 0 }}>
            Annulation du rejet impossible
            <Header.Subheader>Le cycle associé a été encaissé.</Header.Subheader>
          </Header>,
          { type: 'error' },
        );
        return false;
      }

      const response = await onRejetDelete(rejet);

      if (response) {
        toast(`Le rejet de prélèvement de la facture n°${rejet.numero} a été supprimé.`, {
          type: 'success',
        });
      }

      return true;
    },
    [factures, onRejetDelete],
  );

  const handleValidationOpen = React.useCallback(() => {
    setOpenValidation(true);
  }, []);

  const handleValidationClose = React.useCallback(() => {
    setOpenValidation(false);
  }, []);

  // La génération est asynchrone : `response` n'accuse que la prise en compte de la demande.
  // Le toast de succès est émis par GestionPrelevements à la fin du traitement long, à partir
  // du bilan renvoyé par le job — l'annoncer ici serait prématuré.
  const handleValiderClick = React.useCallback(async () => {
    const response = await onValiderClick(formatedFactures);

    if (response) {
      setOpenValidation(false);
      setSelectedCycle(0);

      return true;
    }

    return false;
  }, [formatedFactures, onValiderClick]);

  React.useEffect(() => {
    if (reset) {
      setSelectedItems([]);
      setSelectedCycle(0);
    }
  }, [reset]);

  return (
    <Segment
      basic
      style={{
        marginTop: 0,
      }}
    >
      {mode !== 'rejets' ? (
        <PrelevementsListe
          selectedItems={selectedItems}
          selectedCycle={selectedCycle}
          mode={mode}
          onCycleSelect={handleCycleSelect}
          onItemSelect={handleItemSelect}
          {...(mode === 'rejeter' && { onRejeterClick: handleRejetOpen })}
          {...(mode === 'valider' && { onValiderClick: handleValidationOpen })}
          loading={reset}
        />
      ) : (
        <RejetsListe
          selectedCycle={selectedCycle}
          onCycleSelect={handleCycleSelect}
          onRejetDelete={handleRejetDelete}
          loading={reset}
        />
      )}
      {openValidation && (
        <ModalPrelevementValidation
          nombreFactures={_.size(factures)}
          montantTotal={montantTotal}
          onClose={handleValidationClose}
          onValidate={handleValiderClick}
        />
      )}
      {openRejet && (
        <ModalPrelevementRejet
          nombreRejets={_.size(selectedItems)}
          onClose={handleRejetClose}
          onValidate={handleRejeterClick}
        />
      )}
    </Segment>
  );
};

export default PrelevementTab;
